import { v4 as uuidv4 } from "uuid";
import cogoToast from "cogo-toast";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  apiAddToCart,
  apiClearCart,
  apiFetchCart,
  apiRemoveFromCart,
} from "../../api/cart";

const showToast = (message, type) => {
  cogoToast[type](message, { position: "bottom-left" });
};
const calculateDiscountedPrice = (price, discount) => {
  const numericPrice = Number(price) || 0;
  const numericDiscount = Number(discount) || 0;

  if (numericDiscount <= 0) return numericPrice;

  const discountAmount = (numericPrice * numericDiscount) / 100;
  return +(numericPrice - discountAmount).toFixed(2);
};

const mapApiItemToRedux = (item) => ({
  ...item,
  cartItemId: item.cart_line_item_id || uuidv4(),
  product_id: item.product_id,
  variant_id: item.variant_id,
  name: item.product_title,
  price: item.unit_price,
  quantity: item.quantity,
  slug: item.product_handle,
  discount: item.discount || null,
  images:
    item.product_image_url || item.thumbnail
      ? [item.product_image_url || item.thumbnail]
      : item.images || [],
});

const fetchAndSetCart = createAsyncThunk(
  "cart/fetchAndSetCart",
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const response = await apiFetchCart();
      const items = response.data?.[0]?.items || [];
      const mappedItems = items.map(mapApiItemToRedux);
      dispatch(setCartItems(mappedItems));
      return { success: true };
    } catch (error) {
      console.error("Failed to sync cart after operation:", error);
      dispatch(setCartItems([]));
      return rejectWithValue(error.message);
    }
  }
);

const cartSlice = createSlice({
  name: "cart",
  initialState: {
    cartItems: [],
    loading: "idle",
  },
  reducers: {
    setCartItems(state, action) {
      state.cartItems = action.payload;
    },
    apiOperationSuccess() {},
    apiOperationError() {},
    addToCartInstant(state, action) {
      const newItem = action.payload;
      const normalized = {
        cartItemId: newItem.cartItemId || uuidv4(),
        product_id: newItem.product_id || newItem.id,
        variant_id: newItem.variant_id || newItem.id,
        name: newItem.product_title || newItem.name || newItem.title,
        price: newItem.unit_price || newItem.price || 0,
        quantity: newItem.quantity || 1,
        slug: newItem.product_handle || newItem.slug || "",
        discount: newItem.discount || null,
        images:
          newItem.images || (newItem.thumbnail ? [newItem.thumbnail] : []),
      };

      const existing = state.cartItems.find(
        (item) => item.product_id === normalized.product_id
      );

      if (existing) {
        existing.quantity =
          (existing.quantity || 0) + (normalized.quantity || 1);
      } else {
        state.cartItems.push(normalized);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAndSetCart.pending, (state) => {
        state.loading = "pending";
      })
      .addCase(fetchAndSetCart.fulfilled, (state) => {
        state.loading = "idle";
      })
      .addCase(fetchAndSetCart.rejected, (state) => {
        state.loading = "failed";
      });
  },
});

export const {
  setCartItems,
  apiOperationSuccess,
  apiOperationError,
  addToCartInstant,
} = cartSlice.actions;

const addToCart = createAsyncThunk(
  "cart/addToCart",
  async (payload, { dispatch, rejectWithValue }) => {
    const token = localStorage.getItem("authToken");
    if (!token) {
      showToast("Please login first to add items to cart", "info");
      if (payload?.navigate && typeof payload.navigate === "function") {
        payload.navigate("/login-register");
      }
      return rejectWithValue("Please login to add items to cart");
    }

    const basePrice =
      payload.price || payload.product_price || payload.unit_price || 0;

    const discountPercent = payload.discount || payload.product_discount || 0;

    const finalPrice = calculateDiscountedPrice(basePrice, discountPercent);

    const instantPayload = {
      product_id: payload.product_id || payload.id,
      variant_id: payload.variant_id || payload.id,
      product_title: payload.name || payload.title,
      unit_price: finalPrice, // 👈 ALWAYS discounted
      quantity: payload.quantity || 1,
      slug: payload.slug || "",
      discount: discountPercent,
      images: payload.images || (payload.thumbnail ? [payload.thumbnail] : []),
    };

    dispatch(addToCartInstant(instantPayload));

    if (payload.type === "DECREASE_QUANTITY" && payload.quantity > 0) {
      showToast("Item Decremented From Cart", "warn");
    } else if (payload.type !== "DECREASE_QUANTITY") {
      showToast("Added To Cart", "success");
    }

    const itemDetails = {
      variant_id: payload.variant_id || payload.id,
      quantity: payload.quantity || 1,
      product_id: payload.product_id || payload.id,
      product_title: payload.name || payload.title,
      product_description: payload.description || "",
      product_subtitle: payload.subtitle || payload.name || payload.title,
      product_type: payload.product_type || "General",
      product_collection: payload.collection || "col_default",
      product_handle: payload.slug,
      variant_sku: payload.sku,
      variant_title: payload.variant_title || payload.name || payload.title,
      thumbnail: payload.images?.[0] || payload.thumbnail || "url",
      title: payload.title || payload.name,
      subtitle: payload.subtitle || payload.name,
      unit_price: finalPrice,
    };

    try {
      const response = await apiAddToCart(itemDetails);
      dispatch(fetchAndSetCart());
      return { success: true, message: response.message };
    } catch (error) {
      showToast(error.message || "Cart operation failed.", "error");
      dispatch(fetchAndSetCart());
      return rejectWithValue(error.message);
    }
  }
);

const deleteFromCart = createAsyncThunk(
  "cart/deleteFromCart",
  async (item, { dispatch, rejectWithValue }) => {
    try {
      const response = await apiRemoveFromCart(item.product_id);
      showToast(response.message || `${item.name} removed from cart!`, "error");
      dispatch(fetchAndSetCart());
      return { success: true };
    } catch (error) {
      showToast(error.message || "Failed to remove item.", "error");
      return rejectWithValue(error.message);
    }
  }
);

const decreaseQuantity = createAsyncThunk(
  "cart/decreaseQuantity",
  async (item, { dispatch, rejectWithValue }) => {
    const newQuantity = item.quantity - 1;
    if (newQuantity <= 0) {
      return dispatch(deleteFromCart(item)).unwrap();
    }

    const decreasePayload = {
      ...item,
      quantity: newQuantity,
      type: "DECREASE_QUANTITY",
    };
    try {
      await dispatch(addToCart(decreasePayload)).unwrap();
      return { success: true };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const deleteAllFromCart = createAsyncThunk(
  "cart/deleteAllFromCart",
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const response = await apiClearCart();
      showToast(response.message || "Cart cleared successfully", "error");
      dispatch(fetchAndSetCart());
      return { success: true };
    } catch (error) {
      showToast(error.message || "Failed to clear cart.", "error");
      return rejectWithValue(error.message);
    }
  }
);

export {
  addToCart,
  deleteFromCart,
  decreaseQuantity,
  deleteAllFromCart,
  fetchAndSetCart,
};

export default cartSlice.reducer;
